"use client";

import type { HTMLAttributes, ReactNode } from "react";
import { PanelLeftCloseIcon, PanelLeftIcon } from "lucide-react";
import { cn } from "../../lib";
import { CIRCLE_BUTTON_HOVER_WASH_CLASS } from "./circle-button";
import { GlassCircleChrome } from "./glass-panel";

/** Preferred wide sticky / sidebar panel width (matches `minmax(12rem, 16.5rem)`). */
export const DOCUMENT_SUMMARY_PANEL_WIDTH_REM = 16.5;

export const DOCUMENT_SUMMARY_PANEL_WIDTH_CLASS = "w-[16.5rem]";

export const DOCUMENT_SUMMARY_PANEL_OPEN_WIDTH_CLASS =
	"w-[16.5rem] min-w-[16.5rem]";

export interface DocumentSummaryItem {
	id: string;
	label: string;
	/** When true, item is non-interactive (locked / gated). */
	disabled?: boolean;
}

export interface DocumentSummaryProps {
	items: readonly DocumentSummaryItem[];
	/** Currently active item id (scroll spy / selection). */
	activeId: string | null;
	onItemSelect: (id: string) => void;
	/** Section title — defaults to “On this page”. */
	title?: ReactNode;
	collapsed?: boolean;
	onCollapsedChange?: (collapsed: boolean) => void;
	collapseToggleLabel?: string;
	expandToggleLabel?: string;
	/**
	 * When false, omit the header toggle (host owns it — e.g. top-right HUD).
	 * Default true.
	 */
	showHeaderToggle?: boolean;
	/** When false, omit the “On this page” heading (e.g. sidebar provides its own title). */
	showTitle?: boolean;
	/**
	 * Classes on the collapse/expand toggle wrapper (e.g. absolute gutter
	 * alignment with section chevrons).
	 */
	toggleClassName?: string;
	/**
	 * Max height for the scrollable item list (keep overflow on the list so a
	 * gutter-aligned toggle is not clipped by the sticky column).
	 */
	listMaxHeightClassName?: string;
	className?: string;
	/** Extra classes on the sticky nav shell. */
	navClassName?: string;
	"data-testid"?: string;
}

const TOGGLE_BUTTON_CLASS = cn(
	"relative flex size-full items-center justify-center overflow-hidden rounded-full",
	"[&>*]:relative [&>*]:z-[1]",
	"cursor-pointer text-foreground transition-[opacity,background-color,color] duration-200",
	CIRCLE_BUTTON_HOVER_WASH_CLASS,
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"[&_svg]:!size-4 [&_svg]:block [&_svg]:shrink-0",
	"[&_svg]:[transform:translate3d(0,0,0)] [&_svg]:[backface-visibility:hidden]",
);

const ITEM_LABEL_CLASS =
	"block w-full truncate text-left text-sm leading-6";

const PANEL_TRANSITION_CLASS =
	"overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out";

/**
 * Sticky in-document summary (“On this page”) — Compass-style left track.
 * Active item fades a solid foreground segment on the same rail as the dim
 * track (shared `left-0`, inside overflow). Collapse keeps the toggle mounted
 * and fades / shrinks the panel.
 */
export function DocumentSummary({
	items,
	activeId,
	onItemSelect,
	title = "On this page",
	collapsed = false,
	onCollapsedChange,
	collapseToggleLabel = "Collapse summary",
	expandToggleLabel = "Expand summary",
	showHeaderToggle = true,
	showTitle = true,
	toggleClassName,
	listMaxHeightClassName,
	className,
	navClassName,
	"data-testid": dataTestId = "document-summary",
}: DocumentSummaryProps) {
	const headerToggle =
		showHeaderToggle && onCollapsedChange != null ? (
			<div className={cn("shrink-0", toggleClassName)}>
				<GlassCircleChrome sizeClassName="size-10">
					<button
						type="button"
						className={TOGGLE_BUTTON_CLASS}
						aria-label={
							collapsed ? expandToggleLabel : collapseToggleLabel
						}
						aria-expanded={!collapsed}
						onClick={() => onCollapsedChange(!collapsed)}
						data-testid={
							collapsed
								? `${dataTestId}-expand`
								: `${dataTestId}-collapse`
						}
					>
						{collapsed ? (
							<PanelLeftIcon className="size-4" aria-hidden />
						) : (
							<PanelLeftCloseIcon className="size-4" aria-hidden />
						)}
					</button>
				</GlassCircleChrome>
			</div>
		) : null;

	return (
		<nav
			className={cn(
				"relative flex min-h-10 min-w-0 flex-col",
				navClassName,
				className,
			)}
			aria-label={typeof title === "string" ? title : "Document summary"}
			data-testid={dataTestId}
			data-collapsed={collapsed ? "true" : "false"}
		>
			{headerToggle}
			<div
				className={cn(
					PANEL_TRANSITION_CLASS,
					collapsed
						? "pointer-events-none max-w-0 -translate-x-1 opacity-0"
						: "max-w-[16.5rem] translate-x-0 opacity-100",
				)}
				aria-hidden={collapsed}
			>
				<div
					className={cn(
						"flex flex-col",
						DOCUMENT_SUMMARY_PANEL_WIDTH_CLASS,
					)}
				>
					{showTitle ? (
						<h2 className="flex min-h-10 min-w-0 items-center truncate text-sm font-semibold leading-6 text-foreground">
							{title}
						</h2>
					) : null}
					{/*
					 * Shared track + per-item active segment share the same left edge
					 * (no negative offsets — overflow-y-auto would clip those).
					 */}
					<ul
						className={cn(
							showTitle ? "mt-3" : null,
							"relative min-h-0 overflow-y-auto overscroll-contain",
							"flex flex-col gap-3",
							"text-sm leading-6 text-muted-foreground",
							listMaxHeightClassName,
						)}
					>
						<span
							aria-hidden
							className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-foreground/15"
						/>
						{items.map((item) => {
							const isActive = item.id === activeId;
							const isDisabled = Boolean(item.disabled);

							return (
								<li
									key={item.id}
									data-active={isActive ? "true" : "false"}
									className={cn(
										"group relative pl-4",
										isDisabled && "opacity-45",
									)}
								>
									<span
										aria-hidden
										data-testid={
											isActive
												? `${dataTestId}-active-marker`
												: undefined
										}
										className={cn(
											"pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-foreground",
											"transition-opacity duration-300 ease-out",
											isActive
												? "opacity-100"
												: "opacity-0",
											!isDisabled &&
												!isActive &&
												"group-hover:opacity-45",
										)}
									/>
									{isDisabled ? (
										<span
											className={cn(
												ITEM_LABEL_CLASS,
												"cursor-not-allowed",
											)}
											aria-disabled="true"
											data-testid={`${dataTestId}-item-${item.id}`}
											data-disabled="true"
										>
											{item.label}
										</span>
									) : (
										<button
											type="button"
											tabIndex={collapsed ? -1 : undefined}
											aria-current={
												isActive
													? "location"
													: undefined
											}
											className={cn(
												ITEM_LABEL_CLASS,
												"cursor-pointer transition-colors duration-300 ease-out",
												isActive
													? "font-medium text-foreground"
													: "font-normal text-muted-foreground group-hover:text-foreground",
											)}
											onClick={() =>
												onItemSelect(item.id)
											}
											data-testid={`${dataTestId}-item-${item.id}`}
										>
											{item.label}
										</button>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			</div>
		</nav>
	);
}

export interface DocumentSummaryLayoutProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	/** Summary column content (expanded list or collapsed toggle). */
	summary: ReactNode;
	/** When true, summary panel shrinks/fades; toggle stays in the gutter. */
	summaryCollapsed?: boolean;
	/** Sticky top offset under chrome (drawer / scroll progress). */
	stickyTopClassName?: string;
}

/**
 * Two-column host for document body + sticky {@link DocumentSummary}.
 * Parent must establish `@container`. Place the cover **above** this host so it
 * stays full-bleed. Wide break: `@[720px]` (topic docs cover two-column).
 *
 * First column stays `auto` so the summary panel’s max-width transition drives
 * layout; the gutter toggle is absolutely positioned and does not jump.
 */
export function DocumentSummaryLayout({
	children,
	summary,
	summaryCollapsed = false,
	stickyTopClassName = "top-6",
	className,
	...rest
}: DocumentSummaryLayoutProps) {
	return (
		<div
			className={cn(
				"grid w-full grid-cols-1 items-start gap-8",
				"@[720px]:grid-cols-[auto_minmax(0,1fr)] @[720px]:gap-10",
				className,
			)}
			data-testid="document-summary-layout"
			data-summary-collapsed={summaryCollapsed ? "true" : "false"}
			{...rest}
		>
			<div
				className={cn(
					"sticky hidden min-h-0 self-start @[720px]:block",
					stickyTopClassName,
				)}
				data-slot="document-summary-column"
			>
				{summary}
			</div>
			<div
				className="min-w-0 w-full"
				data-slot="document-summary-content"
			>
				{children}
			</div>
		</div>
	);
}
